import { PaginatedResponse, useApi } from '@/api';
import { Page, PageSkeleton } from '@/components';
import {
  CryptoIcon,
  CurrencyValue,
  DataTable,
  Flex,
  Heading,
  RelativeDateTime,
  TableSkeleton,
  TextSearchFilter,
  useTableProps,
  WalletAddress
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useMemo } from 'react';
import { useParams } from 'react-router';

type WalletsData = {
  address: string;
  block_number: number;
  tx_hash: string;
  order_index: string;
  datetime: string;
  wallet_address: string;
  balance: string;
};

export default function StakingWalletsPage() {
  const { network } = useParams() as {
    network: string;
  };
  const { pagination, sorting, filters } = useTableProps({
    pageSize: 15,
    initialSorting: '-balance',
    filtersDebounceTime: 500,
    initialFilters: {
      textSearch: ''
    }
  });

  const [data, error] = useApi<PaginatedResponse<WalletsData[]>>({
    url: '/sparkstar/staking/holders/',
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      search: filters.filtersApplied['textSearch']
    }
  });
  const columns: TableColumn[] = useMemo(
    () => [
      {
        header: 'Wallet',
        getCell: (row) => <WalletAddress address={row.wallet_address} />,
        sticky: true
      },
      {
        header: 'Staked',
        getCell: (row) => <CurrencyValue value={row.balance} />,
        sortingField: 'balance',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Time',
        getCell: (row) => (
          <Flex gap="xs" color="muted">
            <RelativeDateTime value={row.datetime} />
          </Flex>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'datetime'
      }
    ],
    []
  );
  if (error) {
    throw error;
  }
  if (!data) {
    return (
      <PageSkeleton>
        <TableSkeleton rows={25} columns={4} />
      </PageSkeleton>
    );
  }

  return (
    <Page
      title={
        <Flex flexDirection="row" gap="2xs" alignItems="center">
          <CryptoIcon
            name="SPK"
            address="0xc20059e0317de91738d13af027dfc4a50781b066"
            network={network}
            size="4"
          />
          <Heading>SPK Staking</Heading>
        </Flex>
      }>
      <Flex flexDirection="column" gap="3xs">
        <Flex justifyContent="flex-end">
          <TextSearchFilter
            pagination={pagination}
            placeholder="Search by wallet"
            display="flex"
            flex="1"
            options={{
              variants: {
                textSearchFilter: 'fullWidth'
              }
            }}
            {...filters}
          />
        </Flex>
      </Flex>

      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => row.wallet_address}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    </Page>
  );
}
